import axios from 'axios';

// Resolve API base URL dynamically so LAN access works without config changes.
// - If REACT_APP_API_URL is set at build time, use that.
// - Otherwise derive from the browser's current hostname so both
//   http://localhost:3000  →  http://localhost:5000/api
//   http://192.168.x.x:3000  →  http://192.168.x.x:5000/api
const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:5000/api`;
};

const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 30000,
});

// Attach JWT on every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ─────────────────────────────────────────────────
export const authAPI = {
  login:          data => api.post('/auth/login', data),
  getMe:          ()   => api.get('/auth/me'),
  changePassword: data => api.put('/auth/change-password', data),
  getUsers:       ()   => api.get('/auth/users'),
  createUser:     data => api.post('/auth/users', data),
  toggleUser:     id   => api.patch(`/auth/users/${id}/toggle`),
  updateUser:     (id, data) => api.put(`/auth/users/${id}`, data),
  deleteUser:     id         => api.delete(`/auth/users/${id}`),
};

// ── Dashboard ─────────────────────────────────────────────
export const dashboardAPI = {
  getOverview:         params => api.get('/dashboard/overview', { params }),
  getAttendanceTrend:  params => api.get('/dashboard/attendance-trend', { params }),
  getModuleSummary:    params => api.get('/dashboard/module-summary', { params }),
  getTeacherOverview:  params => api.get('/dashboard/teacher-overview', { params }),
  getAcademicYears:    ()     => api.get('/dashboard/academic-years'),
};

// ── Attendance ────────────────────────────────────────────
export const attendanceAPI = {
  getStats:         params => api.get('/attendance/stats',          { params }),
  getTeacherWise:   params => api.get('/attendance/teacher-wise',   { params }),
  getMonthlyTrend:  params => api.get('/attendance/monthly-trend',  { params }),
  getDailyRecords:  params => api.get('/attendance/daily-records',  { params }),
  getEnhancedStats: params => api.get('/attendance/enhanced-stats', { params }),
  getAnalytics:     params => api.get('/attendance/analytics',      { params }),
  import:           (formData, params) => api.post('/attendance/import', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:           params => api.get('/attendance/export',         { params, responseType: 'blob' }),
  sampleTemplate:   ()     => api.get('/attendance/sample-template',{ responseType: 'blob' }),
};

// ── File Sync (Attendance Time Sheet) ─────────────────────
export const syncAPI = {
  getFolderStatus:   ()  => api.get('/sync/folder-status'),
  uploadAndSync:     fd  => api.post('/sync/upload-attendance', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  scanFolder:        ()  => api.post('/sync/scan-folder'),
  getHistory:        (n) => api.get('/sync/history', { params: { limit: n || 20 } }),
};

// ── Grievances ────────────────────────────────────────────
export const grievancesAPI = {
  getDashboardStats:    params => api.get('/grievances/dashboard-stats', { params }),
  getDetail:            id     => api.get(`/grievances/${id}/detail`),
  sendTelegram:         ()     => api.post('/grievances/telegram/send'),
  getTelegramSchedule:  ()     => api.get('/grievances/telegram/schedule'),
  saveTelegramSchedule: data   => api.put('/grievances/telegram/schedule', data),
  getTelegramBotInfo:      ()          => api.get('/grievances/telegram/bot-info'),
  getTelegramRecipients:   ()          => api.get('/grievances/telegram/recipients'),
  saveTelegramChatId:      data        => api.put('/grievances/telegram/chat-id', data),
  updateAssigneeChatId:    (id, data)  => api.put(`/grievances/telegram/assignee/${id}/chat-id`, data),
  sendAssigneeAlert:       id          => api.post(`/grievances/telegram/assignee/${id}/send`),
  getIndividualSchedule:   ()          => api.get('/grievances/telegram/individual-schedule'),
  saveIndividualSchedule:  data        => api.put('/grievances/telegram/individual-schedule', data),
  getStats:      params => api.get('/grievances/stats', { params }),
  getList:       params => api.get('/grievances', { params }),
  create:        data   => api.post('/grievances', data),
  updateStatus:  (id, data) => api.patch(`/grievances/${id}/status`, data),
  getCategories: ()     => api.get('/grievances/categories'),
  import:        (formData, params) => api.post('/grievances/import', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:        params => api.get('/grievances/export', { params, responseType: 'blob' }),
  sampleTemplate:()     => api.get('/grievances/sample-template', { responseType: 'blob' }),
};

// ── Homework ──────────────────────────────────────────────
export const homeworkAPI = {
  getStats: params => api.get('/homework/stats', { params }),
  getList:  params => api.get('/homework', { params }),
  import:   (formData, params) => api.post('/homework/import', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:   params => api.get('/homework/export', { params, responseType: 'blob' }),
  sampleTemplate: () => api.get('/homework/sample-template', { responseType: 'blob' }),
};

// ── Newsletter ────────────────────────────────────────────
export const newsletterAPI = {
  getStats: params => api.get('/newsletter/stats', { params }),
  getList:  params => api.get('/newsletter', { params }),
  import:   (formData, params) => api.post('/newsletter/import', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:   params => api.get('/newsletter/export', { params, responseType: 'blob' }),
  sampleTemplate: () => api.get('/newsletter/sample-template', { responseType: 'blob' }),
};

// ── Toddle LP Tracking ────────────────────────────────────
export const toddleTrackingAPI = {
  getStats:       params       => api.get('/toddle-tracking/stats',          { params }),
  getFilters:     params       => api.get('/toddle-tracking/filters',         { params }),
  getSchedule:    params       => api.get('/toddle-tracking/schedule',        { params }),
  getList:        params       => api.get('/toddle-tracking',                  { params }),
  create:         data         => api.post('/toddle-tracking',                 data),
  update:         (id, data)   => api.put(`/toddle-tracking/${id}`,           data),
  remove:         id           => api.delete(`/toddle-tracking/${id}`),
  import:         (fd, params) => api.post('/toddle-tracking/import',         fd, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:         params       => api.get('/toddle-tracking/export',          { params, responseType: 'blob' }),
  sampleTemplate: ()           => api.get('/toddle-tracking/sample-template', { responseType: 'blob' }),
  getWeeks:       params       => api.get('/toddle-tracking/weeks',           { params }),
  createWeek:     data         => api.post('/toddle-tracking/weeks',          data),
  updateWeek:     (id, data)   => api.put(`/toddle-tracking/weeks/${id}`,    data),
  deleteWeek:     id           => api.delete(`/toddle-tracking/weeks/${id}`),
};

// ── Lesson Plans ──────────────────────────────────────────
export const lessonPlanAPI = {
  getStats: params => api.get('/lesson-plans/stats', { params }),
  getList:  params => api.get('/lesson-plans', { params }),
  import:   (formData, params) => api.post('/lesson-plans/import', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  export:   params => api.get('/lesson-plans/export', { params, responseType: 'blob' }),
  sampleTemplate: () => api.get('/lesson-plans/sample-template', { responseType: 'blob' }),
};

// ── Observations ─────────────────────────────────────────
export const observationsAPI = {
  getObserversList:   ()         => api.get('/observations/observers-list'),
  getTeachersList:    (params)   => api.get('/observations/teachers-list', { params }),
  getScopeGrades:     ()         => api.get('/observations/scope-grades'),
  getMyObserverInfo:  ()         => api.get('/observations/my-observer-info'),
  getAboutMe:         params     => api.get('/observations/about-me', { params }),
  // Dashboard + pending
  getDashboard:     params      => api.get('/observations/dashboard', { params }),
  getPending:       params      => api.get('/observations/pending', { params }),
  // Observation notification config (admin only)
  getConfig:        params      => api.get('/observations/config', { params }),
  saveConfig:       data        => api.put('/observations/config', data),
  sendTelegramAlert:data        => api.post('/observations/notify', data),
  // Stats & charts
  getStats:         params      => api.get('/observations/stats', { params }),
  getByTeacher:     params      => api.get('/observations/by-teacher', { params }),
  getByObserver:    params      => api.get('/observations/by-observer', { params }),
  getMonthlyTrend:  params      => api.get('/observations/monthly-trend', { params }),
  getWeeklyTrend:   params      => api.get('/observations/weekly-trend', { params }),
  getObservers:     params      => api.get('/observations/observers', { params }),
  getAll:           params      => api.get('/observations', { params }),
  getOne:           id          => api.get(`/observations/${id}`),
  create:           data        => api.post('/observations', data),
  update:           (id, data)  => api.put(`/observations/${id}`, data),
  delete:           id          => api.delete(`/observations/${id}`),
  publish:          id          => api.post(`/observations/${id}/publish`),
  unpublish:        id          => api.post(`/observations/${id}/unpublish`),
  export:           params      => api.get('/observations/export', { params, responseType: 'blob' }),
  import:           formData    => api.post('/observations/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ── Biometric Device ─────────────────────────────────────
// Note: baseURL already includes /api, so paths start without /api prefix
export const biometricAPI = {
  getDevices:          ()          => api.get('/biometric/devices'),
  addDevice:           data        => api.post('/biometric/devices', data),
  updateDevice:        (id, data)  => api.put(`/biometric/devices/${id}`, data),
  deleteDevice:        id          => api.delete(`/biometric/devices/${id}`),
  testConnection:      id          => api.get(`/biometric/devices/${id}/test`),
  pullAttendance:      (id, opts)  => api.post(`/biometric/devices/${id}/pull`, opts || {}),
  pullViaUSB:          id          => api.post(`/biometric/devices/${id}/pull-usb`),
  pullViaDLL:          (id, opts)  => api.post(`/biometric/devices/${id}/pull-dll`, opts || {}),
  autoMap:             id          => api.post(`/biometric/devices/${id}/auto-map`),
  importDeviceFile:    fd          => api.post('/biometric/import-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  syncAll:             ()          => api.post('/biometric/sync-all'),
  getEmployeeMapping:  ()          => api.get('/biometric/employee-mapping'),
  updateBiometricUid:  (empId, d)  => api.put(`/biometric/employees/${empId}/biometric-uid`, d),
  // Auto-sync
  getAutoSyncStatus:   ()          => api.get('/biometric/auto-sync/status'),
  saveAutoSyncConfig:  data        => api.post('/biometric/auto-sync/config', data),
  triggerAutoSync:     ()          => api.post('/biometric/auto-sync/trigger'),
};

// ── Learning Trackers ─────────────────────────────────────
export const trackerAPI = {
  // School-level (all subjects + grades in one file)
  getSchoolTemplate: ()             => api.get('/trackers/school/template', { responseType:'blob' }),
  exportSchoolConfig:()             => api.get('/trackers/school/export',   { responseType:'blob' }),
  importSchoolConfig:fd             => api.post('/trackers/school/import', fd, { headers:{'Content-Type':'multipart/form-data'} }),

  getSubjects:      params          => api.get('/trackers/subjects', { params }),
  getSetupSubjects: params          => api.get('/trackers/setup/subjects', { params }),
  getSubjectConfig: id              => api.get(`/trackers/subjects/${id}/config`),
  createSubject:    data            => api.post('/trackers/subjects', data),
  updateSubject:    (id, data)      => api.put(`/trackers/subjects/${id}`, data),
  deleteSubject:    id              => api.delete(`/trackers/subjects/${id}`),
  copyConfig:       (id, data)      => api.post(`/trackers/subjects/${id}/copy`, data),
  getImportTemplate:()              => api.get('/trackers/import-template', { responseType:'blob' }),
  exportConfig:     id              => api.get(`/trackers/subjects/${id}/export`, { responseType:'blob' }),
  importConfig:     (id, fd)        => api.post(`/trackers/subjects/${id}/import`, fd, { headers:{'Content-Type':'multipart/form-data'} }),
  saveStrands:      (subjectId, d)  => api.put(`/trackers/subjects/${subjectId}/strands`, d),
  saveSubstrands:   (strandId, d)   => api.put(`/trackers/strands/${strandId}/substrands`, d),
  saveWeeks:        (subjectId, d)  => api.put(`/trackers/subjects/${subjectId}/weeks`, d),
  weekTemplate:     subjectId       => api.get(`/trackers/subjects/${subjectId}/weeks/template`, { responseType:'blob' }),
  exportWeeks:      subjectId       => api.get(`/trackers/subjects/${subjectId}/weeks/export`,   { responseType:'blob' }),
  importWeeks:      (subjectId, fd) => api.post(`/trackers/subjects/${subjectId}/weeks/import`, fd, { headers:{'Content-Type':'multipart/form-data'} }),
  getMyClasses:     ()              => api.get('/trackers/my-classes'),
  getClassDashboard:params          => api.get('/trackers/class-dashboard', { params }),
  getEntries:       params          => api.get('/trackers/entries', { params }),
  saveEntries:      data            => api.post('/trackers/entries', data),
  getDashboard:     params          => api.get('/trackers/dashboard', { params }),
  getSchoolSummary:    params => api.get('/trackers/school-summary',    { params }),
  getDashboardCharts:  params => api.get('/trackers/dashboard-charts',  { params }),
  getClassStudents: classId         => api.get(`/trackers/class/${classId}/students`),
};

// ── School Configuration ──────────────────────────────────
export const schoolConfigAPI = {
  getConfig:              ()     => api.get('/school-config'),
  getPublicConfig:        ()     => api.get('/school-config/public'),
  saveConfig:             data   => api.put('/school-config', data),
  getStorageConfig:       ()     => api.get('/school-config/storage'),
  saveStorageConfig:      data   => api.put('/school-config/storage', data),
  testStorageConfig:      data   => api.post('/school-config/storage/test', data),
  getGradeOptions:        ()     => api.get('/school-config/grade-options'),
  // Departments
  getDepartments:         ()     => api.get('/school-config/departments'),
  createDepartment:       data   => api.post('/school-config/departments', data),
  updateDepartment:       (id,d) => api.put(`/school-config/departments/${id}`, d),
  deleteDepartment:       id     => api.delete(`/school-config/departments/${id}`),
  importDepartments:      fd     => api.post('/school-config/departments/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportDepartments:      ()     => api.get('/school-config/departments/export', { responseType: 'blob' }),
  departmentTemplate:     ()     => api.get('/school-config/departments/template', { responseType: 'blob' }),
  // Academic Years
  getAcademicYears:       ()     => api.get('/school-config/academic-years'),
  createAcademicYear:     data   => api.post('/school-config/academic-years', data),
  setActiveAcademicYear:  id     => api.patch(`/school-config/academic-years/${id}/activate`),
  // Designations
  getDesignations:        ()     => api.get('/school-config/designations'),
  createDesignation:      data   => api.post('/school-config/designations', data),
  updateDesignation:      (id,d) => api.put(`/school-config/designations/${id}`, d),
  deleteDesignation:      id     => api.delete(`/school-config/designations/${id}`),
  importDesignations:     fd     => api.post('/school-config/designations/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportDesignations:     ()     => api.get('/school-config/designations/export', { responseType: 'blob' }),
  designationTemplate:    ()     => api.get('/school-config/designations/template', { responseType: 'blob' }),
  // Subjects
  getSubjects:            ()     => api.get('/school-config/subjects'),
  createSubject:          data   => api.post('/school-config/subjects', data),
  updateSubject:          (id,d) => api.put(`/school-config/subjects/${id}`, d),
  deleteSubject:          id     => api.delete(`/school-config/subjects/${id}`),
  importSubjects:         fd     => api.post('/school-config/subjects/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportSubjects:         ()     => api.get('/school-config/subjects/export', { responseType: 'blob' }),
  subjectTemplate:        ()     => api.get('/school-config/subjects/template', { responseType: 'blob' }),
  // Staff
  getStaff:               params => api.get('/school-config/staff', { params }),
  createStaff:            data   => api.post('/school-config/staff', data),
  updateStaff:            (id,d) => api.put(`/school-config/staff/${id}`, d),
  toggleStaff:            id     => api.patch(`/school-config/staff/${id}/toggle`),
  setObserver:            (id,d) => api.patch(`/school-config/staff/${id}/observer`, d),
  deleteStaff:            id     => api.delete(`/school-config/staff/${id}`),
  importStaff:            fd     => api.post('/school-config/staff/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportStaff:            params => api.get('/school-config/staff/export', { params, responseType: 'blob' }),
  staffTemplate:          ()     => api.get('/school-config/staff/template', { responseType: 'blob' }),
  // Students
  getStudents:            params => api.get('/school-config/students', { params }),
  createStudent:          data   => api.post('/school-config/students', data),
  updateStudent:          (id,d) => api.put(`/school-config/students/${id}`, d),
  toggleStudent:          id     => api.patch(`/school-config/students/${id}/toggle`),
  deleteStudent:          id     => api.delete(`/school-config/students/${id}`),
  importStudents:         fd     => api.post('/school-config/students/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportStudents:         params => api.get('/school-config/students/export', { params, responseType: 'blob' }),
  studentTemplate:        ()     => api.get('/school-config/students/template', { responseType: 'blob' }),
  // Logo
  uploadLogo:             fd     => api.post('/school-config/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo:             ()     => api.delete('/school-config/logo'),
  // Holidays
  getHolidays:            params => api.get('/school-config/holidays', { params }),
  createHoliday:          data   => api.post('/school-config/holidays', data),
  updateHoliday:          (id,d) => api.put(`/school-config/holidays/${id}`, d),
  deleteHoliday:          id     => api.delete(`/school-config/holidays/${id}`),
  importHolidays:         (fd,params) => api.post('/school-config/holidays/import', fd, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  exportHolidays:         params => api.get('/school-config/holidays/export', { params, responseType: 'blob' }),
  holidayTemplate:        ()     => api.get('/school-config/holidays/template', { responseType: 'blob' }),
  // Classes
  getClasses:             ()     => api.get('/school-config/classes'),
  createClass:            data   => api.post('/school-config/classes', data),
  updateClass:            (id,d) => api.put(`/school-config/classes/${id}`, d),
  deleteClass:            id     => api.delete(`/school-config/classes/${id}`),
  importClasses:          fd     => api.post('/school-config/classes/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportClasses:          ()     => api.get('/school-config/classes/export', { responseType: 'blob' }),
  classTemplate:          ()     => api.get('/school-config/classes/template', { responseType: 'blob' }),
  // Class teacher & subject teacher
  getClassesWithTeachers:  ()              => api.get('/school-config/classes-with-teachers'),
  setClassTeacher:         (classId, data) => api.put(`/school-config/classes/${classId}/class-teacher`, data),
  getSubjectTeachers:      classId         => api.get(`/school-config/classes/${classId}/subject-teachers`),
  saveSubjectTeachers:     (classId, data) => api.put(`/school-config/classes/${classId}/subject-teachers`, data),
  getTeacherAssignments:   empId           => api.get(`/school-config/employees/${empId}/assignments`),
  // Coordinator mapping & employee hierarchy (Phase 2)
  getCoordinatorMapping:    ()     => api.get('/school-config/coordinator-mapping'),
  addCoordinatorMapping:    data   => api.post('/school-config/coordinator-mapping', data),
  deleteCoordinatorMapping: id     => api.delete(`/school-config/coordinator-mapping/${id}`),
  getEmployeeHierarchy:     ()     => api.get('/school-config/employee-hierarchy'),
  updateEmployeeHierarchy:  (id,d) => api.put(`/school-config/employee-hierarchy/${id}`, d),
};

// ── Roles / RBAC ─────────────────────────────────────────
export const rolesAPI = {
  getAll:         ()         => api.get('/roles'),
  getModules:     ()         => api.get('/roles/modules'),
  create:         data       => api.post('/roles', data),
  update:         (id, data) => api.put(`/roles/${id}`, data),
  delete:         id         => api.delete(`/roles/${id}`),
  setPermissions: (id, data) => api.put(`/roles/${id}/permissions`, { permissions: data }),
  getUsers:       id         => api.get(`/roles/${id}/users`),
};

// ── Employees / Teachers ──────────────────────────────────
export const employeesAPI = {
  getAll:               params   => api.get('/employees', { params }),
  getTeachers:          ()       => api.get('/employees', { params: { staffType: 'teacher' } }),
  getDepartments:       ()       => api.get('/employees/departments'),
  create:               data     => api.post('/employees', data),
  import:               (formData, staffType) => api.post(`/employees/import?staffType=${staffType||'teacher'}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importUsers:          formData => api.post('/employees/import-users', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  export:               params   => api.get('/employees/export', { params, responseType: 'blob' }),
  teacherTemplate:      ()       => api.get('/employees/template/teacher', { responseType: 'blob' }),
  employeeTemplate:     ()       => api.get('/employees/template/employee', { responseType: 'blob' }),
};

// ── Reports ───────────────────────────────────────────────
export const reportsAPI = {
  sendTelegram:   data   => api.post('/reports/send-telegram', data),
  getMissingData: params => api.get('/reports/missing-data', { params }),
};

// ── Edunext ERP Integration ───────────────────────────────
export const erpAPI = {
  getStatus:         ()   => api.get('/erp/status'),
  getEmployees:      ()   => api.get('/erp/employees'),
  syncEmployees:     ()   => api.post('/erp/sync-employees',  {}, { timeout: 180000 }),
  syncAttendance:    data => api.post('/erp/sync-attendance', data, { timeout: 120000 }),
  diagnoseEmployees: ()   => api.get('/erp/diagnose-employees', { timeout: 120000 }),
  syncHomework:      (data) => api.post('/erp/sync-homework', data || {}, { timeout: 120000 }),
  diagnoseHomework:  ()   => api.get('/erp/diagnose-homework',  { timeout: 120000 }),
};

export const morningActivityAPI = {
  getStats:              ()         => api.get('/morning-activity/stats'),
  getActivities:         params     => api.get('/morning-activity', { params }),
  createActivities:      data       => api.post('/morning-activity', data),
  updateActivity:        (id, data) => api.put(`/morning-activity/${id}`, data),
  deleteActivity:        id         => api.delete(`/morning-activity/${id}`),
  markConducted:         id         => api.post(`/morning-activity/${id}/conducted`),
  markNotConducted:      (id, data) => api.post(`/morning-activity/${id}/not-conducted`, data),
  submitEvidence:        (id, fd)   => api.post(`/morning-activity/${id}/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getEvidence:           id         => api.get(`/morning-activity/${id}/evidence`),
  getNotificationConfig: ()         => api.get('/morning-activity/notifications'),
  saveNotificationConfig:data       => api.put('/morning-activity/notifications', data),
  exportReport:          params     => api.get('/morning-activity/export', { params, responseType: 'blob' }),
};

export const timetableAPI = {
  getRooms:          params   => api.get('/timetable/rooms',              { params }),
  createRoom:        data     => api.post('/timetable/rooms',              data),
  updateRoom:        (id, d)  => api.put(`/timetable/rooms/${id}`,         d),
  deleteRoom:        id       => api.delete(`/timetable/rooms/${id}`),
  getConfig:         params   => api.get('/timetable/config',             { params }),
  updateConfig:      data     => api.put('/timetable/config',              data),
  getPeriods:        params   => api.get('/timetable/periods',            { params }),
  upsertPeriods:     data     => api.put('/timetable/periods',             data),
  getGradeConfig:    params   => api.get('/timetable/grade-config',       { params }),
  upsertGradeConfig: data     => api.put('/timetable/grade-config',        data),
  deleteGradeConfig: id       => api.delete(`/timetable/grade-config/${id}`),
  getSlots:          params   => api.get('/timetable/slots',              { params }),
  upsertSlot:        data     => api.post('/timetable/slots',              data),
  deleteSlot:        id       => api.delete(`/timetable/slots/${id}`),
  getTeacherLoad:    params   => api.get('/timetable/teacher-load',       { params }),
  getCompliance:     params   => api.get('/timetable/compliance',         { params }),
  getDashboard:      params   => api.get('/timetable/dashboard-stats',    { params }),
  parsePdf:          formData => api.post('/timetable/import/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  applyImport:       data     => api.post('/timetable/import/apply', data, { timeout: 120000 }),
};

export const performanceAPI = {
  getConfig:      params       => api.get('/performance/config',              { params }),
  updateConfig:   data         => api.put('/performance/config',              data),
  compute:        data         => api.post('/performance/compute',            data),
  getMyScore:     params       => api.get('/performance/my-score',           { params }),
  getDeptSummary: params       => api.get('/performance/department-summary', { params }),
  export:         params       => api.get('/performance/export',             { params, responseType: 'blob' }),
  getScores:         params       => api.get('/performance/scores',             { params }),
  getLeaderboard:    params       => api.get('/performance/leaderboard',        { params }),
  getHRMatrix:       params       => api.get('/performance/hr-matrix',          { params }),
  updateManualScore: (id, data)   => api.put(`/performance/manual-score/${id}`, data),
  getEmployee:       (id, params) => api.get(`/performance/employee/${id}`,     { params }),
};

// ── Lesson Planner Workflow ───────────────────────────────
export const plannerAPI = {
  // Plans
  getPlans:          params       => api.get('/lesson-planner/plans', { params }),
  getStats:          params       => api.get('/lesson-planner/plans/stats', { params }),
  getCompliance:     params       => api.get('/lesson-planner/plans/compliance', { params }),
  getPlan:           id           => api.get(`/lesson-planner/plans/${id}`),
  createPlan:        data         => api.post('/lesson-planner/plans', data),
  updatePlan:        (id, data)   => api.put(`/lesson-planner/plans/${id}`, data),
  deletePlan:        id           => api.delete(`/lesson-planner/plans/${id}`),
  submitPlan:        id           => api.post(`/lesson-planner/plans/${id}/submit`),
  // Stages
  getStages:         id           => api.get(`/lesson-planner/plans/${id}/stages`),
  updateStage:       (id, n, data) => api.put(`/lesson-planner/plans/${id}/stages/${n}`, data),
  // Unit Planner
  getUnit:           id           => api.get(`/lesson-planner/plans/${id}/unit`),
  updateUnit:        (id, data)   => api.put(`/lesson-planner/plans/${id}/unit`, data),
  // Assessments
  getAssessments:    id           => api.get(`/lesson-planner/plans/${id}/assessments`),
  addAssessment:     (id, data)   => api.post(`/lesson-planner/plans/${id}/assessments`, data),
  updateAssessment:  (id, aid, d) => api.put(`/lesson-planner/plans/${id}/assessments/${aid}`, d),
  deleteAssessment:  (id, aid)    => api.delete(`/lesson-planner/plans/${id}/assessments/${aid}`),
  // Coordinator Review
  getPendingReviews: ()           => api.get('/lesson-planner/reviews/pending'),
  submitReview:      (planId, d)  => api.post(`/lesson-planner/reviews/${planId}`, d),
  // VP Approval
  getPendingApprovals: ()         => api.get('/lesson-planner/approvals/pending'),
  submitApproval:    (planId, d)  => api.post(`/lesson-planner/approvals/${planId}`, d),
  // Config (admin)
  getConfig:         (params)     => api.get('/lesson-planner/config', { params }),
  updateConfig:      (data)       => api.put('/lesson-planner/config', data),
  triggerSla:        ()           => api.post('/lesson-planner/config/trigger-sla'),
  // Dashboard
  getDashboard:      (params)     => api.get('/lesson-planner/dashboard', { params }),
  // Execution
  saveExecution:     (id, data)   => api.put(`/lesson-planner/plans/${id}/execution`, data),
  // Reflection
  saveReflection:    (id, data)   => api.put(`/lesson-planner/plans/${id}/reflection`, data),
  // Observations
  getObservations:   (params)     => api.get('/lesson-planner/observations', { params }),
  createObservation: (data)       => api.post('/lesson-planner/observations', data),
  updateObservation: (oid, data)  => api.put(`/lesson-planner/observations/${oid}`, data),
  acknowledgeObservation: (oid)   => api.post(`/lesson-planner/observations/${oid}/acknowledge`),
  // Notifications
  getNotifications:  (params)     => api.get('/lesson-planner/notifications', { params }),
  markRead:          (nid)        => api.put(`/lesson-planner/notifications/${nid}/read`),
  markAllRead:       ()           => api.put('/lesson-planner/notifications/read-all'),
};

// ── Email Notifications ───────────────────────────────────
export const emailNotificationAPI = {
  getRules:    params     => api.get('/email-notifications',          { params }),
  createRule:  data       => api.post('/email-notifications',         data),
  updateRule:  (id, data) => api.put(`/email-notifications/${id}`,    data),
  deleteRule:  id         => api.delete(`/email-notifications/${id}`),
  toggleRule:  id         => api.patch(`/email-notifications/${id}/toggle`),
  getLogs:     params     => api.get('/email-notifications/logs',     { params }),
  testSend:    id         => api.post(`/email-notifications/${id}/test`),
};

// ── Telegram Per-Module Notifications ────────────────────
export const telegramNotifyAPI = {
  getStaff:      ()         => api.get('/telegram-notify/staff'),
  getRoles:      ()         => api.get('/telegram-notify/roles'),
  getConfigs:    params     => api.get('/telegram-notify/configs',                { params }),
  createConfig:  data       => api.post('/telegram-notify/configs',               data),
  updateConfig:  (id, data) => api.put(`/telegram-notify/configs/${id}`,          data),
  deleteConfig:  id         => api.delete(`/telegram-notify/configs/${id}`),
  toggleConfig:  id         => api.patch(`/telegram-notify/configs/${id}/toggle`),
  testSend:      id         => api.post(`/telegram-notify/configs/${id}/test`),
  getLogs:       params     => api.get('/telegram-notify/logs',                   { params }),
};

// ── Leave Management ──────────────────────────────────────
export const leaveAPI = {
  // Leave Types
  getTypes:          ()       => api.get('/leave/types'),
  createType:        data     => api.post('/leave/types', data),
  updateType:        (id, d)  => api.put(`/leave/types/${id}`, d),
  deleteType:        id       => api.delete(`/leave/types/${id}`),
  // Balances
  getBalances:       params   => api.get('/leave/balances', { params }),
  initBalances:      data     => api.post('/leave/balances/initialize', data),
  adjustBalance:     (id, d)  => api.put(`/leave/balances/${id}`, d),
  // Applications
  getApplications:   params   => api.get('/leave/applications', { params }),
  apply:             data     => api.post('/leave/applications', data),
  review:            (id, d)  => api.put(`/leave/applications/${id}/review`, d),
  cancel:            id       => api.put(`/leave/applications/${id}/cancel`),
  // Calendar
  getCalendar:       params   => api.get('/leave/calendar', { params }),
};

// ── Parent Teacher Connect (PTC) ──────────────────────────
export const ptcAPI = {
  // Campaigns
  getCampaigns:    params       => api.get('/ptc/campaigns', { params }),
  createCampaign:  data         => api.post('/ptc/campaigns', data),
  updateCampaign:  (id, data)   => api.put(`/ptc/campaigns/${id}`, data),
  deleteCampaign:  id           => api.delete(`/ptc/campaigns/${id}`),
  publish:         id           => api.post(`/ptc/campaigns/${id}/publish`),
  close:           id           => api.post(`/ptc/campaigns/${id}/close`),
  // Analytics
  getSummary:      id           => api.get(`/ptc/campaigns/${id}/summary`),
  getClassDetail:  (id, cls)    => api.get(`/ptc/campaigns/${id}/class/${encodeURIComponent(cls)}`),
  // Responses
  getResponses:    (id, params) => api.get(`/ptc/campaigns/${id}/responses`, { params }),
  createResponse:  (id, data)   => api.post(`/ptc/campaigns/${id}/responses`, data),
  deleteResponse:  (cid, rid)   => api.delete(`/ptc/campaigns/${cid}/responses/${rid}`),
  // Tokens (admin)
  getTokens:       id           => api.get(`/ptc/campaigns/${id}/tokens`),
  generateTokens:  id           => api.post(`/ptc/campaigns/${id}/tokens/generate`),
  deleteToken:     (cid, tid)   => api.delete(`/ptc/campaigns/${cid}/tokens/${tid}`),
  // Portal (public — no JWT needed, use axiosPublic below)
  // Tickets
  getTickets:      (id, params) => api.get(`/ptc/campaigns/${id}/tickets`, { params }),
  createTicket:    (id, data)   => api.post(`/ptc/campaigns/${id}/tickets`, data),
  updateTicket:    (cid, tid, d)=> api.put(`/ptc/campaigns/${cid}/tickets/${tid}`, d),
  deleteTicket:    (cid, tid)   => api.delete(`/ptc/campaigns/${cid}/tickets/${tid}`),
  getComments:     tid          => api.get(`/ptc/tickets/${tid}/comments`),
  addComment:      (tid, data)  => api.post(`/ptc/tickets/${tid}/comments`, data),
};

export const enrichmentAPI = {
  // Lookups
  getEmployees:  ()            => api.get('/enrichment/employees'),
  getGrades:     (year_id)     => api.get('/enrichment/grades', { params: { year_id } }),
  getStudents:   (params)      => api.get('/enrichment/students', { params }),
  getDashboard:  (year_id)     => api.get('/enrichment/dashboard', { params: { year_id } }),
  // Programmes
  getProgrammes:  (year_id)    => api.get('/enrichment/programmes', { params: { year_id } }),
  createProgramme:(d)          => api.post('/enrichment/programmes', d),
  updateProgramme:(id, d)      => api.put(`/enrichment/programmes/${id}`, d),
  deleteProgramme:(id)         => api.delete(`/enrichment/programmes/${id}`),
  // Enrollments
  getEnrollments: (pid)        => api.get(`/enrichment/programmes/${pid}/enrollments`),
  enroll:         (pid, d)     => api.post(`/enrichment/programmes/${pid}/enrollments`, d),
  removeEnroll:   (pid, eid)   => api.delete(`/enrichment/programmes/${pid}/enrollments/${eid}`),
  // Sessions
  getSessions:    (pid)        => api.get(`/enrichment/programmes/${pid}/sessions`),
  createSession:  (pid, d)     => api.post(`/enrichment/programmes/${pid}/sessions`, d),
  deleteSession:  (pid, sid)   => api.delete(`/enrichment/programmes/${pid}/sessions/${sid}`),
  getSessionAtt:  (sid, pid)   => api.get(`/enrichment/sessions/${sid}/attendance`, { params: { programme_id: pid } }),
  markAttendance: (sid, d)     => api.post(`/enrichment/sessions/${sid}/attendance`, d),
  getAttSummary:  (pid)        => api.get(`/enrichment/programmes/${pid}/attendance-summary`),
  // Templates
  getTemplates:   (pid)        => api.get(`/enrichment/programmes/${pid}/templates`),
  createTemplate: (pid, d)     => api.post(`/enrichment/programmes/${pid}/templates`, d),
  deleteTemplate: (pid, tid)   => api.delete(`/enrichment/programmes/${pid}/templates/${tid}`),
  // Scores
  getScores:      (pid)        => api.get(`/enrichment/programmes/${pid}/scores`),
  saveScore:      (pid, sid, d)=> api.put(`/enrichment/programmes/${pid}/scores/${sid}`, d),
  bulkSaveScores: (pid, d)     => api.post(`/enrichment/programmes/${pid}/scores/bulk`, d),
  // Analytics
  getAnalytics:   (pid)        => api.get(`/enrichment/programmes/${pid}/analytics`),
};

// ── Public (unauthenticated) client for feedback portal ───
const publicApi = axios.create({ baseURL: resolveBaseURL(), timeout: 30000 });

export const feedbackPortalAPI = {
  getByToken:  token      => publicApi.get(`/ptc/feedback/${token}`),
  saveDraft:   (token, d) => publicApi.post(`/ptc/feedback/${token}/draft`, d),
  submit:      (token, d) => publicApi.post(`/ptc/feedback/${token}/submit`, d),
};

// ── PTC Booking (admin + slot management) ─────────────────────
export const ptcBookingAPI = {
  // Setup data (classes + employees for dropdowns)
  getSetupData:     (cid)        => api.get(`/ptc/campaigns/${cid}/setup-data`),
  // Slot configs
  getSlotConfigs:   (cid)        => api.get(`/ptc/campaigns/${cid}/slot-configs`),
  createSlotConfig: (cid, d)     => api.post(`/ptc/campaigns/${cid}/slot-configs`, d),
  updateSlotConfig: (cid, id, d) => api.put(`/ptc/campaigns/${cid}/slot-configs/${id}`, d),
  deleteSlotConfig: (cid, id)    => api.delete(`/ptc/campaigns/${cid}/slot-configs/${id}`),
  // Slot generation
  generateSlots:    (cid, d)     => api.post(`/ptc/campaigns/${cid}/generate-slots`, d || {}),
  getSlots:         (cid, p)     => api.get(`/ptc/campaigns/${cid}/slots`, { params: p }),
  clearSlots:       (cid, p)     => api.delete(`/ptc/campaigns/${cid}/slots`, { params: p }),
  // Booking tokens
  generateBookingTokens: (cid, d) => api.post(`/ptc/campaigns/${cid}/booking-tokens/generate`, d || {}),
  getBookingTokens: (cid)        => api.get(`/ptc/campaigns/${cid}/booking-tokens`),
  deleteBookingToken:(cid, tid)  => api.delete(`/ptc/campaigns/${cid}/booking-tokens/${tid}`),
  // Bookings
  getBookings:      (cid, p)     => api.get(`/ptc/campaigns/${cid}/bookings`, { params: p }),
  updateBooking:    (cid, bid, d)=> api.put(`/ptc/campaigns/${cid}/bookings/${bid}`, d),
  getBookingStats:  (cid)        => api.get(`/ptc/campaigns/${cid}/booking-stats`),
  // Teacher
  getMyMeetings:    (p)          => api.get('/ptc/my-meetings', { params: p }),
};

// ── Notification Thresholds (per-module) ─────────────────────
export const notificationThresholdsAPI = {
  get:  (module, yearId)       => api.get(`/notification-thresholds/${module}`, { params: { academic_year_id: yearId } }),
  save: (module, data)         => api.patch(`/notification-thresholds/${module}`, data),
};

// ── Public booking portal (no auth) ──────────────────────────
export const bookingPortalAPI = {
  getPortal: token      => publicApi.get(`/ptc/book/${token}`),
  book:      (token, d) => publicApi.post(`/ptc/book/${token}/book`, d),
  cancel:    (token, d) => publicApi.post(`/ptc/book/${token}/cancel`, d),
};

// ── RBAC / User Access Control ───────────────────────────────
export const rbacAPI = {
  listUsers:             ()             => api.get('/rbac/users'),
  listRoles:             ()             => api.get('/rbac/roles'),
  getGradesSections:     ()             => api.get('/rbac/grades-sections'),
  getSubjects:           ()             => api.get('/rbac/subjects'),
  getUserScope:          userId         => api.get(`/rbac/user-scope/${userId}`),
  addUserScope:          data           => api.post('/rbac/user-scope', data),
  removeUserScope:       id             => api.delete(`/rbac/user-scope/${id}`),
  clearUserScope:        userId         => api.delete(`/rbac/user-scope/clear/${userId}`),
  setUserRole:           (userId, role) => api.put(`/rbac/users/${userId}/role`, { role }),
  getRolePermissions:    roleName       => api.get(`/rbac/role-permissions/${roleName}`),
  updateRolePermission:  data           => api.post('/rbac/role-permissions', data),
  getMyScope:                  (module)       => api.get('/rbac/my-scope', { params: { module } }),
  getMyDashboard:              ()             => api.get('/rbac/my-dashboard'),
  setSchoolDashboardAccess:    (userId, access) => api.put(`/rbac/users/${userId}/school-dashboard-access`, { access }),
};

export const dailyReportsAPI = {
  getReports:      params        => api.get('/daily-reports',                    { params }),
  getReport:       id            => api.get(`/daily-reports/${id}`),
  createReport:    data          => api.post('/daily-reports', data),
  updateReport:    (id, data)    => api.put(`/daily-reports/${id}`, data),
  deleteReport:    id            => api.delete(`/daily-reports/${id}`),
  publishReport:   (id, status)  => api.patch(`/daily-reports/${id}/publish`, { status }),
  updateFollowUp:  (id, itemId, payload) => api.patch(`/daily-reports/${id}/items/${itemId}/follow-up`, typeof payload === 'string' ? { followUpStatus: payload } : payload),
  getFollowUps:    params        => api.get('/daily-reports/follow-ups',         { params }),
  getSettings:        ()             => api.get('/daily-reports/settings'),
  saveSettings:       data           => api.post('/daily-reports/settings', data),
  getStats:           params         => api.get('/daily-reports/stats', { params }),
  getAttachments:     id             => api.get(`/daily-reports/${id}/attachments`),
  uploadAttachment:   (id, formData) => api.post(`/daily-reports/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment:   (id, attId)    => api.delete(`/daily-reports/${id}/attachments/${attId}`),
};

export const transportAPI = {
  // Dashboard
  getDashboard:          ()           => api.get('/transport/dashboard'),

  // Reference data
  getEmployees:          ()           => api.get('/transport/employees'),
  getStudents:           ()           => api.get('/transport/students'),

  // Vehicle Master
  getVehicles:           ()           => api.get('/transport/vehicles'),
  createVehicle:         data         => api.post('/transport/vehicles', data),
  updateVehicle:         (id, data)   => api.put(`/transport/vehicles/${id}`, data),
  deleteVehicle:         id           => api.delete(`/transport/vehicles/${id}`),

  // Route Master
  getRoutes:             ()           => api.get('/transport/routes'),
  createRoute:           data         => api.post('/transport/routes', data),
  updateRoute:           (id, data)   => api.put(`/transport/routes/${id}`, data),
  deleteRoute:           id           => api.delete(`/transport/routes/${id}`),

  // Stop Management
  getStops:              params       => api.get('/transport/stops', { params }),
  createStop:            data         => api.post('/transport/stops', data),
  updateStop:            (id, data)   => api.put(`/transport/stops/${id}`, data),
  deleteStop:            id           => api.delete(`/transport/stops/${id}`),

  // Student Transport Mapping
  getStudentMappings:    params       => api.get('/transport/student-mapping', { params }),
  createStudentMapping:  data         => api.post('/transport/student-mapping', data),
  updateStudentMapping:  (id, data)   => api.put(`/transport/student-mapping/${id}`, data),
  deleteStudentMapping:  id           => api.delete(`/transport/student-mapping/${id}`),

  // Attendance
  getVehicleAttendance:  params       => api.get('/transport/vehicle-attendance', { params }),
  saveVehicleAttendance: data         => api.post('/transport/vehicle-attendance', data),
  getDriverAttendance:   params       => api.get('/transport/driver-attendance', { params }),
  saveDriverAttendance:  data         => api.post('/transport/driver-attendance', data),
  getHelperAttendance:   params       => api.get('/transport/helper-attendance', { params }),
  saveHelperAttendance:  data         => api.post('/transport/helper-attendance', data),
  getStudentAttendance:  params       => api.get('/transport/student-attendance', { params }),
  saveStudentAttendance: data         => api.post('/transport/student-attendance', data),
  bulkMarkStudents:      data         => api.post('/transport/student-attendance/bulk', data),

  // Phase 2 — GPS
  getGpsLive:            ()           => api.get('/transport/gps/live'),
  pushGpsUpdate:         data         => api.post('/transport/gps/push', data),
  manualGpsEntry:        data         => api.post('/transport/gps/manual', data),
  getGpsHistory:         (id, params) => api.get(`/transport/gps/history/${id}`, { params }),

  // Phase 2 — ANPR & Gate Log
  getAnprLog:            params       => api.get('/transport/anpr-log', { params }),
  createAnprEntry:       data         => api.post('/transport/anpr-log', data),
  getGateLog:            params       => api.get('/transport/gate-log', { params }),
  createGateEntry:       data         => api.post('/transport/gate-log', data),

  // Phase 2 — Analytics
  getAnalytics:          params       => api.get('/transport/analytics', { params }),

  // Phase 2 — Exceptions
  getExceptions:         params       => api.get('/transport/exceptions', { params }),
  createException:       data         => api.post('/transport/exceptions', data),
  updateException:       (id, data)   => api.put(`/transport/exceptions/${id}`, data),

  // Phase 3 — Notifications
  getNotificationLog:        params   => api.get('/transport/notifications', { params }),
  getNotificationSettings:   ()       => api.get('/transport/notification-settings'),
  updateNotificationSettings:data     => api.put('/transport/notification-settings', data),
  sendTestNotification:      data     => api.post('/transport/notification-test', data),

  // Phase 3 — Reports
  getDailyReport:            params   => api.get('/transport/reports/daily-attendance', { params }),
  getRouteReport:            params   => api.get('/transport/reports/route', { params }),
  getMonthlyReport:          params   => api.get('/transport/reports/monthly', { params }),
  getVehicleComplianceReport:params   => api.get('/transport/reports/vehicle-compliance', { params }),
  getStudentTransportReport: ()       => api.get('/transport/reports/students'),

  // Phase 4 — Parent Portal (admin endpoints)
  generateParentToken:       (student_id) => api.post(`/transport/parent-tokens/${student_id}/generate`),
  sendParentPortalSms:       (student_id) => api.post(`/transport/parent-tokens/${student_id}/send-sms`),
  listParentTokens:          ()           => api.get('/transport/parent-tokens'),
  revokeParentToken:         (id)         => api.put(`/transport/parent-tokens/${id}/revoke`),

  // Phase 4 — Route Optimization
  getOptimizationSuggestions:     params => api.get('/transport/optimization/suggestions', { params }),
  generateOptimizationSuggestions:()     => api.post('/transport/optimization/generate'),
  updateOptimizationSuggestion:   (id, data) => api.put(`/transport/optimization/${id}`, data),
  getRouteEfficiency:             ()     => api.get('/transport/optimization/efficiency'),

  // Phase 6 — Fee Management
  getFeeStructures:     ()         => api.get('/transport/fees/structures'),
  createFeeStructure:   data       => api.post('/transport/fees/structures', data),
  updateFeeStructure:   (id, data) => api.put(`/transport/fees/structures/${id}`, data),
  deleteFeeStructure:   id         => api.delete(`/transport/fees/structures/${id}`),

  getStudentFees:       params     => api.get('/transport/fees/assignments', { params }),
  assignFee:            data       => api.post('/transport/fees/assign', data),
  bulkAssignFees:       data       => api.post('/transport/fees/assign/bulk', data),

  recordPayment:        data       => api.post('/transport/fees/payments', data),
  getPaymentHistory:    params     => api.get('/transport/fees/payments', { params }),
  cancelPayment:        id         => api.put(`/transport/fees/payments/${id}/cancel`),

  getOutstandingDues:   params     => api.get('/transport/fees/outstanding', { params }),
  getFeeReport:         params     => api.get('/transport/fees/report', { params }),

  // CCTV Auto Attendance (manual frame upload)
  cctvAttendance:       data     => api.post('/transport/attendance/cctv', data),

  // Phase 7 — Live CCTV
  getCCTVCameras:       ()       => api.get('/transport/cctv/cameras'),
  createCCTVCamera:     data     => api.post('/transport/cctv/cameras', data),
  updateCCTVCamera:     (id,data)=> api.put(`/transport/cctv/cameras/${id}`, data),
  deleteCCTVCamera:     id       => api.delete(`/transport/cctv/cameras/${id}`),
  startCCTVMonitoring:  id       => api.post(`/transport/cctv/cameras/${id}/start`),
  stopCCTVMonitoring:   id       => api.post(`/transport/cctv/cameras/${id}/stop`),
  captureCCTVSnapshot:  id       => api.post(`/transport/cctv/cameras/${id}/snapshot`),
  getCCTVStreamStatus:  ()       => api.get('/transport/cctv/streams/status'),
  getCCTVEvents:        params   => api.get('/transport/cctv/events', { params }),
  getCCTVStats:         ()       => api.get('/transport/cctv/stats'),

  // Phase 5 — ANPR & Face Recognition
  recognitionHealth:    ()       => api.get('/transport/recognition/health'),
  recognizePlate:       data     => api.post('/transport/recognition/plate', data),
  recognizeFace:        data     => api.post('/transport/recognition/face', data),
  enrollFace:           data     => api.post('/transport/recognition/faces/enroll', data),
  listEnrolledFaces:    ()       => api.get('/transport/recognition/faces'),
  revokeEnrolledFace:   id       => api.delete(`/transport/recognition/faces/${id}`),
  getRecognitionLog:    params   => api.get('/transport/recognition/log', { params }),
};

export default api;
